/**
 * 올린 HTML 을 화면에 맞춘다(2026-10-20) — 만든 사람은 어느 화면에 나갈지 모른다.
 *
 * 올린 HTML 은 고객사 사람들이 저마다 만든다. 화면 크기를 따라 늘고 주게(%·vw·flex) 만든 것은 어느 화면에서나 맞지만,
 * 자기 모니터 크기(1920×1080·1366×768…)에 픽셀로 고정해 만든 것은 다른 화면에서 오른쪽·아래가 잘리거나 빈다. 사람에게
 * 해상도를 묻지 않고 **우리가 잰다**:
 *
 * 1. **잰다** — 콘솔이 올린 HTML 을 크기가 다른 두 창(1920×1080·1280×960)에 띄우고, 문서에 끼운 스크립트
 *    (`HTML_MEASURE_SCRIPT`)가 내용이 차지한 상자를 알려 준다(`postMessage`). 샌드박스 안에서 돌고 부모에게 숫자만 보낸다.
 * 2. **가른다**(`classifyHtmlLayout`) — 창을 따라 내용 크기가 바뀌면 "늘고 주는" HTML 이라 그대로 둔다. 두 창에서 같으면
 *    고정 크기다 — 그 크기를 기준 크기로 둔다(자산의 `width`·`height`).
 * 3. **맞춘다**(`htmlFitBox`) — 화면은 고정 크기 HTML 을 기준 크기로 그린 뒤 화면에 다 들어가게(맞춤) 또는 꽉 차게(채움)
 *    통째로 줄이거나 늘려 가운데 둔다. 템플릿 캔버스와 같은 방법이다(`renderTemplateStage`).
 *
 * 배치를 새로 짜 주지는 못한다 — 가로로 만든 고정 크기를 세로 화면에 맞추면 위아래가 빈다(채움이면 옆이 잘린다).
 */
import { prependToHtml } from './html-sandbox.js';
/** 콘텐츠의 화면 맞춤 설정 — 맞춤(기본: 고정 크기면 다 보이게 줄이거나 늘린다)·채움(꽉 차게, 넘치는 쪽은 잘린다)·그대로. */
export const HTML_FITS = ['auto', 'cover', 'none'];
/** 기준 크기의 범위 — 너무 작거나 큰 값은 잘못 잰 것이다. */
export const HTML_SIZE_MIN = 64;
export const HTML_SIZE_MAX = 7680;
/** 재생 항목에 실을 맞춤 — 기준 크기를 모르거나(늘고 주는 HTML) "그대로" 면 없다. */
export function itemHtmlFit(setting, width, height) {
    if (!width || !height || setting === 'none')
        return undefined;
    return setting === 'cover' ? 'cover' : 'contain';
}
/** 기준 크기의 문서를 화면에 놓을 배율과 자리(왼쪽 위 기준, `transform-origin: 0 0`). */
export function htmlFitBox(design, screen, mode) {
    const sx = screen.width / design.width;
    const sy = screen.height / design.height;
    const scale = mode === 'cover' ? Math.max(sx, sy) : Math.min(sx, sy);
    return {
        scale,
        left: (screen.width - design.width * scale) / 2,
        top: (screen.height - design.height * scale) / 2,
    };
}
/**
 * 기준 크기의 문서에서 한 영역(비율 0~1)을 상자에 맞추는 배율과 자리(2026-11-03, 반반 화면의 HTML 칸) — 그 영역이 상자 가운데에
 * 다 들어오게(맞춤) 또는 꽉 차게(채움). 영역 밖의 문서도 상자 안에 들어오면 보인다(잘라 내지 않는다 — 칸이 자른다). 영역이
 * 전체면 `htmlFitBox` 와 같다.
 */
export function htmlAreaBox(design, area, box, mode) {
    const part = area ?? { x: 0, y: 0, w: 1, h: 1 };
    const width = design.width * part.w;
    const height = design.height * part.h;
    const sx = box.width / width;
    const sy = box.height / height;
    const scale = mode === 'cover' ? Math.max(sx, sy) : Math.min(sx, sy);
    return {
        scale,
        left: (box.width - width * scale) / 2 - design.width * part.x * scale,
        top: (box.height - height * scale) / 2 - design.height * part.y * scale,
    };
}
/** 재는 스크립트가 부모에게 보내는 말의 이름. */
export const HTML_MEASURE_MESSAGE = 'signageone:html-size';
/**
 * 문서에 끼우는 재기 스크립트 — 다 읽고 0.4초·1.6초 뒤 두 번 잰다(스크립트로 그리는 배너가 자리를 잡을 때까지). 두 번째에
 * `final: true`.
 *
 * 내용 상자는 `body` 아래 세 단계까지의 보이는 요소를 합친 것이다. 더 깊이 내려가지 않고, 넘치면 감추는 상자(`overflow`)의
 * 자식은 보지 않는다 — 무대 밖에서 기다리는 애니메이션 조각이 상자를 부풀리지 않게. 문서 왼쪽·위로 삐져나간 것은 화면에도
 * 안 보이니 0 에서 자른다. 옛 문법(ES5)으로 쓴다.
 */
export const HTML_MEASURE_SCRIPT = `(function(){var T=${JSON.stringify(HTML_MEASURE_MESSAGE)};
function walk(el,d,box){var k=el.children;for(var i=0;i<k.length&&i<2000;i++){var c=k[i],t=c.tagName;
if(t==='SCRIPT'||t==='STYLE'||t==='TEMPLATE'||t==='NOSCRIPT'||t==='META'||t==='LINK')continue;
var s=window.getComputedStyle(c);if(s.display==='none'||s.visibility==='hidden'||s.opacity==='0')continue;
var r=c.getBoundingClientRect();if(r.width>=2&&r.height>=2){var x=window.pageXOffset||0,y=window.pageYOffset||0;
var l=Math.max(0,r.left+x),tp=Math.max(0,r.top+y),rt=r.right+x,b=r.bottom+y;
if(rt>l&&b>tp){if(!box.v)box.v={left:l,top:tp,right:rt,bottom:b};else{box.v.left=Math.min(box.v.left,l);box.v.top=Math.min(box.v.top,tp);box.v.right=Math.max(box.v.right,rt);box.v.bottom=Math.max(box.v.bottom,b);}}}
var clip=/(hidden|clip|auto|scroll)/.test(s.overflowX+' '+s.overflowY);if(d<3&&!clip)walk(c,d+1,box);}}
function deep(el,d,box,n){var k=el.children;for(var i=0;i<k.length;i++){if(++n.c>4000)return;var c=k[i],t=c.tagName;
if(t==='SCRIPT'||t==='STYLE'||t==='TEMPLATE'||t==='NOSCRIPT'||t==='META'||t==='LINK')continue;
var s=window.getComputedStyle(c);if(s.display==='none'||s.visibility==='hidden'||s.opacity==='0')continue;
var r=c.getBoundingClientRect();if(r.width>=2&&r.height>=2){var x=window.pageXOffset||0,y=window.pageYOffset||0;
var l=Math.max(0,r.left+x),tp=Math.max(0,r.top+y),rt=r.right+x,b=r.bottom+y;
if(rt>l&&b>tp){if(!box.f)box.f={left:l,top:tp,right:rt,bottom:b};else{box.f.left=Math.min(box.f.left,l);box.f.top=Math.min(box.f.top,tp);box.f.right=Math.max(box.f.right,rt);box.f.bottom=Math.max(box.f.bottom,b);}
if(b<window.innerHeight-1.5&&(box.n===null||b>box.n))box.n=b;}}
if(d<40)deep(c,d+1,box,n);}}
function send(f){try{var box={v:null,f:null,n:null};if(document.body){walk(document.body,1,box);deep(document.body,1,box,{c:0});}
parent.postMessage({type:T,box:box.v,full:box.f,near:box.n,vw:window.innerWidth,vh:window.innerHeight,final:f},'*');}catch(e){}}
function go(){setTimeout(function(){send(false);},400);setTimeout(function(){send(true);},1600);}
if(document.readyState==='complete')go();else window.addEventListener('load',go);})();`;
/** 재기 스크립트를 끼운 문서 — 콘솔이 잴 때만 쓴다(화면에는 끼우지 않는다). */
export function measuringHtml(html) {
    return prependToHtml(html, `<script>${HTML_MEASURE_SCRIPT}</script>`);
}
/** 잴 때 띄우는 두 창 — 폭·높이·비율이 모두 달라야 "창을 따라 바뀌는가" 를 두 축 모두 본다. */
export const HTML_MEASURE_VIEWPORTS = [
    { width: 1920, height: 1080 },
    { width: 1280, height: 960 },
];
/** 같은 크기로 볼 오차(px) — 반올림·스크롤 막대 몫. */
const SAME = 8;
/**
 * 두 창에서 잰 값으로 가른다.
 *
 * - 내용 크기가 창을 따라 바뀌었다(어느 한 축이라도) → 늘고 주는 HTML. 그대로 둔다.
 * - 크기가 같고 두 창 모두에서 가운데에 있으며 작은 창에도 다 들어간다 → 가운데 맞춤으로 이미 화면에 맞는다. 그대로.
 * - 크기가 같다 → 고정 크기. 기준 크기는 내용 상자에 왼쪽·위 여백을 양쪽에 더한 것(기본 여백 8px 같은 것) — 두 창 중
 *   작은 여백을 쓴다(가운데 맞춘 고정 무대는 작은 창에서 여백이 0 이 된다).
 * - 그릴 것이 없거나 기준 크기가 범위 밖이면 그대로.
 */
export function classifyHtmlLayout(a, b) {
    if (!a.box || !b.box)
        return { kind: 'responsive' };
    const wa = a.box.right - a.box.left;
    const ha = a.box.bottom - a.box.top;
    const wb = b.box.right - b.box.left;
    const hb = b.box.bottom - b.box.top;
    if (Math.abs(wa - wb) > SAME || Math.abs(ha - hb) > SAME)
        return { kind: 'responsive' };
    const centered = (box, vw) => box.left > SAME && Math.abs(box.left - (vw - box.right)) <= SAME;
    const small = Math.max(wa, wb) <= Math.min(a.vw, b.vw) + SAME && Math.max(ha, hb) <= Math.min(a.vh, b.vh) + SAME;
    if (centered(a.box, a.vw) && centered(b.box, b.vw) && small)
        return { kind: 'responsive' };
    const marginX = Math.max(0, Math.min(a.box.left, b.box.left));
    const marginY = Math.max(0, Math.min(a.box.top, b.box.top));
    const width = Math.round(Math.max(wa, wb) + 2 * marginX);
    const height = Math.round(Math.max(ha, hb) + 2 * marginY);
    if (width < HTML_SIZE_MIN || height < HTML_SIZE_MIN || width > HTML_SIZE_MAX || height > HTML_SIZE_MAX) {
        return { kind: 'responsive' };
    }
    return { kind: 'fixed', width, height };
}
//# sourceMappingURL=html-fit.js.map